import { ModalType } from '../../pages/CockpitPage';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Dialog } from '../ui/Dialog';
import { useMutation } from '@tanstack/react-query';
import { AdminClient } from '../../services/AdminClient';
import { useContext, useState } from 'react';
import { UserContext } from '../../context/UserProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faCheckCircle, faExclamationTriangle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';

type ImportDialogType = {
    open: boolean;
    setOpenModal: (modalType: ModalType | null) => void;
};

type ImportResult = {
    importedTransactionsCount: number;
    skippedRowsCount: number;
    failedRowsCount: number;
    failedRows: { rowNumber: number; reason: string }[];
};

export const ImportDialog = ({ open, setOpenModal }: ImportDialogType) => {
    const user = useContext(UserContext);
    const [file, setFile] = useState<File>(new File([], ''));
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    const mutation = useMutation({
        mutationFn: (data: { file: File }) => {
            return AdminClient(user).uploadFile(data.file);
        },
    });

    const resetState = () => {
        setImportResult(null);
        setError(null);
        setFile(new File([], ''));
    };

    const handleClose = () => {
        resetState();
        setOpenModal(null);
    };

    const onSubmit = async () => {
        setError(null);
        setImportResult(null);

        mutation.mutate(
            { file },
            {
                onSuccess: async (data) => {
                    if (!data.ok) {
                        setError('Er is een fout opgetreden bij het importeren');
                        return;
                    }

                    const json = await data.json();
                    setImportResult(json);
                },
                onError: () => {
                    setError('Er is een fout opgetreden bij het importeren');
                },
            },
        );
    };

    const showForm = !importResult && !error;
    const hasFailedRows = importResult && importResult.failedRowsCount > 0;
    const allSuccessful = importResult && importResult.failedRowsCount === 0;

    return (
        <Dialog open={open} onClose={() => null} hideCloseButton={true}>
            <button className="absolute top-6 right-6">
                <FontAwesomeIcon icon={faClose} className="text-2xl text-primary" onClick={handleClose} />
            </button>
            <Dialog.Title className="mb-3">Importeren tankbeurten</Dialog.Title>

            {showForm && (
                <>
                    <Input
                        placeholder="Bestand"
                        type="file"
                        required
                        onChange={(e) => {
                            setFile(e.target.files![0]);
                        }}
                    />

                    <div className="flex justify-end pt-6">
                        <Button loading={mutation.isPending} onClick={onSubmit}>
                            Importeren
                        </Button>
                    </div>
                </>
            )}

            {error && (
                <div className="text-center py-4">
                    <FontAwesomeIcon icon={faTimesCircle} className="text-4xl text-red-500 mb-4" />
                    <p className="text-red-600 font-medium">{error}</p>
                    <div className="flex justify-end pt-6">
                        <Button onClick={resetState}>Opnieuw proberen</Button>
                    </div>
                </div>
            )}

            {allSuccessful && (
                <div className="text-center py-4">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-4xl text-green-500 mb-4" />
                    <p className="text-lg font-medium text-green-700">
                        {importResult.importedTransactionsCount} transacties geïmporteerd
                    </p>
                    {importResult.skippedRowsCount > 0 && (
                        <p className="text-gray-500">
                            {importResult.skippedRowsCount} overgeslagen (reeds geïmporteerd)
                        </p>
                    )}
                    <div className="flex justify-end pt-6">
                        <Button onClick={handleClose}>Sluiten</Button>
                    </div>
                </div>
            )}

            {hasFailedRows && (
                <>
                    <div className="flex items-center gap-2 mb-4">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-2xl text-amber-500" />
                        <div>
                            <p className="font-medium text-green-700">
                                {importResult.importedTransactionsCount} transacties geïmporteerd
                            </p>
                            {importResult.skippedRowsCount > 0 && (
                                <p className="text-gray-500">
                                    {importResult.skippedRowsCount} overgeslagen (reeds geïmporteerd)
                                </p>
                            )}
                            <p className="font-medium text-amber-600">
                                {importResult.failedRowsCount} rijen mislukt
                            </p>
                        </div>
                    </div>

                    <div className="border rounded-md max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Rij</th>
                                    <th className="text-left px-3 py-2 font-medium">Reden</th>
                                </tr>
                            </thead>
                            <tbody>
                                {importResult.failedRows.map((row) => (
                                    <tr key={row.rowNumber} className="border-t">
                                        <td className="px-3 py-2">{row.rowNumber}</td>
                                        <td className="px-3 py-2">{row.reason}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-end pt-6">
                        <Button onClick={handleClose}>Sluiten</Button>
                    </div>
                </>
            )}
        </Dialog>
    );
};
